import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { ElementIcon, ElementIconType } from "@/components/ui/icons";
import { ReactComponent as PanoPreviewSvg } from "@/components/ui/icons/pano-view.svg";
import { Pano360PreviewCanvas } from "@/components/ui/pano-360-preview-canvas";
import { getCoordinateStringZUp } from "@/components/ui/scene-context-menu";
import {
  FaroPopover,
  FaroSvgIcon,
  FaroText,
  grey,
  NO_TRANSLATE_CLASS,
  MarkupPolygonIcon,
  WorldCoordinateIcon,
} from "@faro-lotv/flat-ui";
import {
  convertToDateString,
  GUID,
  isLoading,
  Loading,
  MaybeLoading,
  SignedUrl,
} from "@faro-lotv/foundation";
import {
  IElementTypeHint,
  ISOTimeString,
  URI,
  type IElementImg360,
} from "@faro-lotv/ielement-types";
import { useValidUrl } from "@faro-lotv/project-source";
import {
  ButtonBase,
  CardMedia,
  Collapse,
  Fade,
  PopperProps,
  Skeleton,
  useTheme,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import { useMemo, useState, type JSX } from "react";
import { Vector3Tuple } from "three";
import {
  AiDescriptionSection,
  CoordinatesSection,
  PREVIEW_SECTION_SX,
} from "./capture-preview-sections";

/** The size of the preview in px */
const PREVIEW_SIZE = { width: 400, imageHeight: 180, padding: 12 };

/**
 * Repositions the preview whenever its size changes, e.g. when the description or the pano
 * finish loading, so it stays inside the screen. Popper only repositions on scroll and window resize
 */
const UPDATE_ON_RESIZE_MODIFIER = {
  name: "updateOnResize",
  enabled: true,
  phase: "main" as const,
  fn: () => undefined,
  effect: ({
    state,
    instance,
  }: {
    state: { elements: { popper: HTMLElement } };
    instance: { update(): unknown };
  }) => {
    const observer = new ResizeObserver(() => void instance.update());
    observer.observe(state.elements.popper);
    return () => observer.disconnect();
  },
};

/** The size of the capture preview in px, wide enough for real-world coordinates */
const CAPTURE_PREVIEW_SIZE = { width: 320, imageHeight: 150, padding: 12 };

export type PlaceholderPreviewBaseProps = Pick<
  PopperProps,
  "anchorEl" | "placement" | "disablePortal"
> & {
  /** The name of the placeholder. */
  name?: string;

  /** The URL to the image to render. */
  imageUri?: URI | SignedUrl | null | Loading;

  /** The pano element to render. */
  panoElement?: IElementImg360;

  /** Whether the required pano object is available */
  hasPanoramaMedia: boolean;

  /** The reason why the scan is disabled */
  disabledReason?: string;

  /** ISO 8601 timestamp when the element has been created. */
  createdAt?: ISOTimeString;

  /** Whether the preview should be visible */
  isVisible?: boolean;

  /**
   * Called when the pointer enters or leaves the preview popover itself.
   * Allows the parent to keep the preview open while the user hovers over it.
   */
  onPreviewHoverChange?(isHovered: boolean): void;

  /** The name of the dataset the placeholder belongs to. */
  datasetName?: string;

  /** The type of the dataset the placeholder belongs to. */
  datasetType?: IElementTypeHint;

  /** The coordinates (y-up) of the placeholder. */
  coordinates?: Vector3Tuple;

  /** Additional modifiers to add to the popper component. */
  popperModifiers?: PopperProps["modifiers"];

  /** The annotations taken inside this capture */
  annotations?: Array<{ id: GUID; title: string }>;

  /** Callback issued when one of the annotations is clicked */
  onAnnotationClick?(id: GUID): void;

  /** Color of the annotation symbol, to keep its original colors on the dark background */
  annotationIconColor?: string;

  /** Icon of the capture, as in the project tree, shown before its name */
  icon?: ElementIconType;

  /**
   * True for the capture preview of the tree and the views: AI description, annotations and
   * coordinates with a project/real-world toggle, without the dataset
   */
  isCapturePreview?: boolean;

  /** The description the AI wrote for the capture image */
  aiDescription?: MaybeLoading<string>;

  /** The capture image the AI description was written for, to review it */
  aiDescriptionImageId?: GUID;
};

/**
 * @returns  A dialog that displays a preview of an Img360 placeholder on hover,
 * while handling warm-up and cool-down transitions. The warm-up transition
 * introduces a delay before showing the preview window upon hovering,
 * ensuring a smooth and deliberate interaction. The cool-down transition
 * introduces a delay before hiding the preview window after moving the
 * pointer away, preventing flickering or abrupt disappearance.
 *
 * These transitions contribute to a better user experience by providing a
 * subtle delay for previewing and dismissing the content, reducing visual
 * noise and creating a more polished interaction.
 */
export function PlaceholderPreviewBase({
  name,
  imageUri,
  panoElement,
  hasPanoramaMedia,
  disabledReason,
  createdAt,
  isVisible,
  onPreviewHoverChange,
  anchorEl,
  datasetName,
  datasetType,
  coordinates,
  popperModifiers,
  annotations,
  onAnnotationClick,
  icon,
  annotationIconColor,
  isCapturePreview = false,
  aiDescription,
  aiDescriptionImageId,
  disablePortal = true,
  placement = "bottom",
}: PlaceholderPreviewBaseProps): JSX.Element | null {
  const theme = useTheme();
  /**
   * The duration used for both fade in/out of window as well as the warm up and cool down time to make the window visible/invisible
   * i.e handles the delay between pointer hover and the popup visibility both while hovering in (warm-up) and hovering out (cool-down)
   */
  const transitionDuration = theme.transitions.duration.shorter;

  const [isPreviewLoading, setIsPreviewImageLoading] = useState(true);

  const isImageLoading =
    !hasPanoramaMedia &&
    (isLoading(imageUri) || (isPreviewLoading && !!imageUri));
  const cardImage =
    hasPanoramaMedia || isLoading(imageUri)
      ? undefined
      : (imageUri ?? undefined);
  const signedCardImage = useValidUrl(cardImage);

  // Keep the preview open while hovered, to interact with the pano, the annotations list or the coordinates
  const isInteractive =
    hasPanoramaMedia || !!annotations?.length || isCapturePreview;

  const size = isCapturePreview ? CAPTURE_PREVIEW_SIZE : PREVIEW_SIZE;

  const distanceUnit = useDistanceUnit();
  const formattedCoordinates = useMemo(() => {
    if (coordinates) {
      return getCoordinateStringZUp(coordinates, distanceUnit, true);
    }
  }, [coordinates, distanceUnit]);

  return (
    <Fade in={isVisible} timeout={transitionDuration}>
      {/** This div is needed to fix an issue with Fade: https://github.com/mui/material-ui/issues/27154#issuecomment-1117386458 */}
      <Box component="div">
        <FaroPopover
          open
          dark
          disablePortal={disablePortal}
          placement={placement}
          anchorEl={anchorEl}
          showCloseButton={false}
          sx={{
            // Remove default padding around content and make it fit the content
            " .MuiPaper-root": {
              // Remove padding on top, between content and arrow
              "> .MuiStack-root": {
                gap: 0,
              },
              p: 0,
              maxWidth: "fit-content",
            },
            // Have the arrow pass events through to the popover content.
            "& .MuiPopper-arrow": {
              pointerEvents: "none",
            },
          }}
          modifiers={
            isCapturePreview
              ? [...(popperModifiers ?? []), UPDATE_ON_RESIZE_MODIFIER]
              : popperModifiers
          }
        >
          <Box
            component="div"
            onPointerEnter={
              isInteractive ? () => onPreviewHoverChange?.(true) : undefined
            }
            onPointerLeave={
              isInteractive ? () => onPreviewHoverChange?.(false) : undefined
            }
            // The preview is rendered through a React portal, so events still
            // bubble through the React tree to ancestors. Stop pointer/click
            // events here so interacting with the preview doesn't trigger
            // selection or other side effects.
            onPointerDown={(ev) => ev.stopPropagation()}
            onMouseDown={(ev) => ev.stopPropagation()}
            onClick={(ev) => ev.stopPropagation()}
            sx={{
              width: size.width,
              background: grey[950],
              padding: `${size.padding}px`,
              borderRadius: "4px",
            }}
          >
            {isCapturePreview ? (
              <CaptureHeader icon={icon} name={name} createdAt={createdAt} />
            ) : (
              <Stack
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: "10px",
                }}
              >
                <FaroText
                  className={NO_TRANSLATE_CLASS}
                  variant="labelM"
                  color={grey[100]}
                >
                  {name}
                </FaroText>
                <FaroText
                  className={NO_TRANSLATE_CLASS}
                  variant="bodyXS"
                  color={grey[100]}
                >
                  {createdAt ? convertToDateString(createdAt) : undefined}
                </FaroText>
              </Stack>
            )}

            {isImageLoading && (
              <Skeleton
                variant="rectangular"
                width="100%"
                height={size.imageHeight}
                sx={{ background: grey[800] }}
              />
            )}

            {hasPanoramaMedia && !!panoElement ? (
              <Box component="div" sx={{ position: "relative" }}>
                <Pano360PreviewCanvas
                  panoElement={panoElement}
                  height={size.imageHeight}
                />
                <Box
                  component="div"
                  sx={{
                    position: "absolute",
                    bottom: 4,
                    right: 8,
                    // so it doesn't block panorama interaction
                    pointerEvents: "none",
                  }}
                >
                  <FaroSvgIcon
                    source={PanoPreviewSvg}
                    aria-label="pano preview icon"
                    fontSize="large"
                  />
                </Box>
              </Box>
            ) : (
              <CardMedia
                component="img"
                onLoad={() => setIsPreviewImageLoading(false)}
                onError={() => setIsPreviewImageLoading(false)}
                image={signedCardImage}
                sx={{
                  width: "100%",
                  height: size.imageHeight,
                  objectFit: "fill",
                  display: !isImageLoading && !!imageUri ? "block" : "none",
                }}
              />
            )}
            {disabledReason && !isImageLoading && (
              <FaroText variant="bodyS" dark>
                {disabledReason}
              </FaroText>
            )}
            {isCapturePreview && (
              <>
                <AiDescriptionSection
                  description={aiDescription}
                  imageId={aiDescriptionImageId}
                />
                {!!annotations?.length && (
                  <Box component="div" sx={PREVIEW_SECTION_SX}>
                    <PreviewAnnotationsInfo
                      annotations={annotations}
                      onAnnotationClick={onAnnotationClick}
                      annotationIconColor={annotationIconColor}
                    />
                  </Box>
                )}
                {coordinates && (
                  <CoordinatesSection coordinates={coordinates} />
                )}
              </>
            )}
            {!isCapturePreview && (
              <PreviewBottomInfo
                datasetName={datasetName}
                datasetType={datasetType}
                coordinates={formattedCoordinates}
              />
            )}
          </Box>
        </FaroPopover>
      </Box>
    </Fade>
  );
}

/** @returns the header of the capture preview: tree icon, name and date */
function CaptureHeader({
  icon,
  name,
  createdAt,
}: Pick<
  PlaceholderPreviewBaseProps,
  "icon" | "name" | "createdAt"
>): JSX.Element {
  return (
    <Stack direction="row" sx={{ gap: 1, alignItems: "center", mb: 1 }}>
      {icon !== undefined && (
        <ElementIcon icon={icon} sx={{ fontSize: "16px", color: grey[100] }} />
      )}
      <FaroText
        className={NO_TRANSLATE_CLASS}
        variant="labelM"
        color={grey[100]}
        noWrap
        title={name}
        sx={{ flex: 1, minWidth: 0 }}
      >
        {name}
      </FaroText>
      {createdAt && (
        <FaroText variant="bodyS" color={grey[300]} noWrap>
          {convertToDateString(createdAt)}
        </FaroText>
      )}
    </Stack>
  );
}

/** @returns an annotation of the preview list, clickable to go to it */
function AnnotationItem({
  id,
  title,
  onClick,
}: {
  /** Id of the annotation */
  id: GUID;
  /** Title of the annotation */
  title: string;
  /** Called when the annotation is clicked */
  onClick?(id: GUID): void;
}): JSX.Element {
  return (
    <ButtonBase
      onClick={() => onClick?.(id)}
      sx={{
        justifyContent: "flex-start",
        pl: "18px",
        pr: 0.5,
        py: 0.25,
        borderRadius: "4px",
        "&:hover": { backgroundColor: grey[800] },
      }}
    >
      <FaroText
        className={NO_TRANSLATE_CLASS}
        variant="bodyS"
        color={grey[300]}
        noWrap
      >
        {title}
      </FaroText>
    </ButtonBase>
  );
}

/** Maximum number of annotation names listed in the preview */
const MAX_LISTED_ANNOTATIONS = 3;

/** @returns the list of the annotations taken inside a capture, each clickable to go to it */
function PreviewAnnotationsInfo({
  annotations,
  onAnnotationClick,
  annotationIconColor,
}: Pick<
  PlaceholderPreviewBaseProps,
  "annotations" | "onAnnotationClick" | "annotationIconColor"
>): JSX.Element | null {
  const [isExpanded, setIsExpanded] = useState(false);
  if (!annotations?.length) return null;

  const hiddenCount = annotations.length - MAX_LISTED_ANNOTATIONS;
  return (
    <Stack sx={{ gap: 0.25 }}>
      <Stack direction="row" sx={{ gap: 0.5, alignItems: "center", mb: 0.25 }}>
        <MarkupPolygonIcon
          sx={{ fontSize: "16px", color: annotationIconColor }}
        />
        <FaroText variant="labelM" color={grey[100]}>
          {annotations.length === 1
            ? "1 annotation"
            : `${annotations.length} annotations`}
        </FaroText>
      </Stack>
      <Stack sx={{ maxHeight: 120, overflowY: "auto" }}>
        {annotations.slice(0, MAX_LISTED_ANNOTATIONS).map(({ id, title }) => (
          <AnnotationItem
            key={id}
            id={id}
            title={title}
            onClick={onAnnotationClick}
          />
        ))}
        {/* The other annotations slide in, so the preview grows smoothly instead of jumping */}
        <Collapse in={isExpanded}>
          <Stack>
            {annotations.slice(MAX_LISTED_ANNOTATIONS).map(({ id, title }) => (
              <AnnotationItem
                key={id}
                id={id}
                title={title}
                onClick={onAnnotationClick}
              />
            ))}
          </Stack>
        </Collapse>
      </Stack>
      {hiddenCount > 0 && (
        <ButtonBase
          onClick={() => setIsExpanded((expanded) => !expanded)}
          sx={{ justifyContent: "flex-start", pl: "18px", py: 0.25 }}
        >
          <FaroText variant="bodyS" color={grey[400]}>
            {isExpanded ? "Show less" : `+${hiddenCount} more`}
          </FaroText>
        </ButtonBase>
      )}
    </Stack>
  );
}

type PreviewBottomInfoProps = Pick<
  PlaceholderPreviewBaseProps,
  "datasetName" | "datasetType"
> & {
  /** The formatted coordinates of the pano */
  coordinates?: string;
};

function PreviewBottomInfo({
  datasetName,
  datasetType,
  coordinates,
}: PreviewBottomInfoProps): JSX.Element | null {
  if (!datasetName && !coordinates) return null;

  const icon =
    datasetType === IElementTypeHint.dataSetVideoWalk
      ? ElementIconType.DataSessionVideoRecording
      : ElementIconType.PointCloudIcon;

  return (
    <Stack direction="row" sx={{ alignItems: "flex-start", mt: "10px" }}>
      <Stack
        direction="row"
        sx={{ gap: 0.5, alignItems: "flex-start", width: "50%" }}
      >
        {datasetName && (
          <ElementIcon
            icon={icon}
            sx={{ color: grey[100], fontSize: "14px" }}
          />
        )}
        <FaroText
          className={NO_TRANSLATE_CLASS}
          variant="bodyXS"
          color={grey[100]}
        >
          {datasetName}
        </FaroText>
      </Stack>
      <Stack
        direction="row"
        sx={{
          gap: 0.5,
          alignItems: "flex-start",
          width: "50%",
          justifyContent: "flex-end",
        }}
      >
        {coordinates && (
          <WorldCoordinateIcon sx={{ color: grey[100], fontSize: "14px" }} />
        )}
        <FaroText
          className={NO_TRANSLATE_CLASS}
          variant="bodyXS"
          color={grey[100]}
        >
          {coordinates}
        </FaroText>
      </Stack>
    </Stack>
  );
}
